import type { Id, ScriptDocument } from "@supermanus/document-model";
import {
  buildManuscriptExport,
  suggestedExportFilename,
  type ExportBlock,
  type ExportCell,
  type ExportRun,
  type ManuscriptExportModel,
  type ManuscriptExportOptions,
} from "./manuscriptExport";
import {
  ACCENT,
  BORDER,
  CELL_PAD_X,
  CELL_PAD_Y,
  CONTENT_W_TWIP,
  DURATION_COL_TWIP,
  FONT_SANS,
  FONT_SANS_FALLBACK,
  FS_BODY,
  FS_CHARACTER,
  FS_COL_HEAD,
  FS_DURATION,
  FS_HEADER,
  FS_META,
  FS_SCENE_NUM,
  FS_SCENE_TITLE,
  FOOTER_TWIP,
  HEADER_TWIP,
  hexRgb,
  INK,
  LINE_240,
  MARGIN_BOTTOM_TWIP,
  MARGIN_LEFT_TWIP,
  MARGIN_RIGHT_TWIP,
  MARGIN_TOP_TWIP,
  MUTED,
  PAGE_H_TWIP,
  PAGE_W_TWIP,
  PARA_GAP_TWIP,
  RULE,
} from "./printPageLayout";

export type { ManuscriptExportOptions };

function rtfEscape(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/\{/g, "\\{")
    .replace(/\}/g, "\\}")
    .replace(/\r\n/g, "\\line ")
    .replace(/\n/g, "\\line ")
    .replace(/\r/g, "\\line ")
    .replace(/[^\x00-\x7F]/g, (chars) =>
      [...chars]
        .map((ch) => {
          const code = ch.codePointAt(0)!;
          return code <= 0xffff ? `\\u${code}?` : "";
        })
        .join(""),
    );
}

class RtfColors {
  private list: string[] = [];

  index(hex: string): number {
    const key = hex.replace(/^#/, "").toUpperCase();
    const found = this.list.indexOf(key);
    if (found >= 0) return found + 1;
    this.list.push(key);
    return this.list.length;
  }

  table(): string {
    const entries = this.list.map((hex) => {
      const rgb = hexRgb(hex);
      if (!rgb) return "\\red0\\green0\\blue0;";
      return `\\red${rgb.r}\\green${rgb.g}\\blue${rgb.b};`;
    });
    return `{\\colortbl;${entries.join("")}}`;
  }
}

function runToRtf(run: ExportRun, colors: RtfColors, color?: string): string {
  if (run.break) return "\\line ";
  let inner = rtfEscape(run.text);
  if (run.bold) inner = `\\b ${inner}\\b0 `;
  if (run.italic) inner = `\\i ${inner}\\i0 `;
  if (run.underline) inner = `\\ul ${inner}\\ulnone `;
  if (run.strike) inner = `\\strike ${inner}\\strike0 `;
  if (run.super) inner = `\\super ${inner}\\nosupersub `;
  if (run.sub) inner = `\\sub ${inner}\\nosupersub `;
  const cf = run.color ?? color;
  if (cf) inner = `\\cf${colors.index(cf)} ${inner}\\cf${colors.index(INK)} `;
  return inner;
}

function blocksToRtf(
  blocks: ExportBlock[],
  colors: RtfColors,
  opts: { intbl: boolean; fs?: number; color?: string; sa?: number },
): string {
  const fs = opts.fs ?? FS_BODY;
  const sa = opts.sa ?? PARA_GAP_TWIP;
  const prefix = opts.intbl
    ? `\\pard\\intbl\\sl${LINE_240}\\slmult1\\fs${fs}\\sa${sa} `
    : `\\pard\\sl${LINE_240}\\slmult1\\fs${fs}\\sa${sa} `;
  if (blocks.length === 0) return `${prefix}\\par`;
  return blocks
    .map((block, i) => {
      const last = i === blocks.length - 1;
      const saHere = last ? 0 : sa;
      const pfx = opts.intbl
        ? `\\pard\\intbl\\sl${LINE_240}\\slmult1\\fs${fs}\\sa${saHere} `
        : `\\pard\\sl${LINE_240}\\slmult1\\fs${fs}\\sa${saHere} `;
      const text = block.runs.map((r) => runToRtf(r, colors, opts.color)).join("");
      return `${pfx}${text || "\\~"}\\par`;
    })
    .join("");
}

function cellBorders(opts: {
  bottom?: boolean;
  right?: boolean;
  colors: RtfColors;
}): string {
  const parts: string[] = [];
  if (opts.bottom) {
    parts.push(
      `\\clbrdrb\\brdrs\\brdrw10\\brdrcf${opts.colors.index(RULE)}`,
    );
  }
  if (opts.right) {
    parts.push(
      `\\clbrdrr\\brdrs\\brdrw10\\brdrcf${opts.colors.index(RULE)}`,
    );
  }
  return parts.join("");
}

function cellContent(cell: ExportCell, colors: RtfColors): string {
  if (cell.merge === "continue") return "\\pard\\intbl\\fs22 ";
  const parts: string[] = [];
  if (cell.meta?.length) {
    parts.push(
      `\\pard\\intbl\\sl${LINE_240}\\slmult1\\fs${FS_META}\\b\\cf${colors.index(MUTED)} ${rtfEscape(cell.meta.join("  "))}\\b0\\cf${colors.index(INK)}\\par`,
    );
  }
  if (cell.characterName) {
    parts.push(
      `\\pard\\intbl\\sl${LINE_240}\\slmult1\\fs${FS_CHARACTER}\\b ${rtfEscape(cell.characterName)}\\b0\\par`,
    );
  }
  parts.push(
    blocksToRtf(cell.blocks, colors, {
      intbl: true,
      color: cell.color,
      sa: PARA_GAP_TWIP,
    }),
  );
  return parts.join("");
}

function rtfHeaderFooter(model: ManuscriptExportModel, colors: RtfColors): string {
  const ink = colors.index(INK);
  const muted = colors.index(MUTED);
  const title = model.productionTitle
    ? `{\\b\\cf${ink} ${rtfEscape(model.productionTitle)}}`
    : "";
  const episode = model.episodeLine
    ? `{\\cf${muted} ${rtfEscape(model.episodeLine)}}`
    : "";
  const when = `{\\cf${muted} ${rtfEscape(model.printedAtLabel)}}`;
  return (
    `{\\header\\pard\\sl${LINE_240}\\slmult1\\fs${FS_HEADER}\\tqr\\tx${CONTENT_W_TWIP} ${title}\\tab ${when}\\par` +
    `\\pard\\sl${LINE_240}\\slmult1\\fs${FS_HEADER} ${episode}\\par}` +
    `{\\footer\\pard\\qc\\sl${LINE_240}\\slmult1\\fs${FS_HEADER}\\cf${muted} ${rtfEscape("Side ")}\\chpgn / {\\field{\\*\\fldinst NUMPAGES}{\\fldrslt 1}}\\par}`
  );
}

function sceneHeadRtf(scene: ManuscriptExportModel["scenes"][0], colors: RtfColors): string {
  const bg = colors.index(scene.headerBg);
  const w = CONTENT_W_TWIP;
  const durW = DURATION_COL_TWIP;
  const titleW = w - durW;
  const num = scene.heading
    ? `Scene ${scene.number} ${scene.heading}`
    : `Scene ${scene.number}`;
  return (
    `\\trowd\\trgaph0\\trleft0\\trpaddl${CELL_PAD_X}\\trpaddr${CELL_PAD_X}\\trpaddt${CELL_PAD_Y}\\trpaddb${CELL_PAD_Y}` +
    `\\trpaddfl3\\trpaddfr3\\trpaddft3\\trpaddfb3` +
    `\\clcbpat${bg}\\clvertalt\\cellx${titleW}` +
    `\\clcbpat${bg}\\clvertalt\\cellx${w}` +
    `\\pard\\intbl\\sl${LINE_240}\\slmult1\\fs${FS_SCENE_NUM}\\b ${rtfEscape(num)}\\b0\\par` +
    `\\pard\\intbl\\sl${LINE_240}\\slmult1\\fs${FS_SCENE_TITLE}\\b ${rtfEscape(scene.title)}\\b0\\cell` +
    `\\pard\\intbl\\qr\\sl${LINE_240}\\slmult1\\fs${FS_DURATION}\\cf${colors.index(ACCENT)} ${rtfEscape(scene.duration)}\\cf${colors.index(INK)}\\cell\\row`
  );
}

function scriptTableRtf(model: ManuscriptExportModel, colors: RtfColors): string {
  const colCount = model.columnLabels.length;
  const colW = Math.floor(CONTENT_W_TWIP / colCount);
  const xs = model.columnLabels.map((_, i) => colW * (i + 1));
  const pad =
    `\\trpaddl${CELL_PAD_X}\\trpaddr${CELL_PAD_X}\\trpaddt${CELL_PAD_Y}\\trpaddb${CELL_PAD_Y}` +
    `\\trpaddfl3\\trpaddfr3\\trpaddft3\\trpaddfb3`;

  const parts: string[] = [];
  for (const scene of model.scenes) {
    parts.push(sceneHeadRtf(scene, colors));
    parts.push("\\pard\\par");

    const headDefs = model.columnLabels
      .map((_, i) => {
        const right = model.showColumnRule && i === 0 && colCount > 1;
        return `${cellBorders({ bottom: true, right, colors })}\\clvertalt\\cellx${xs[i]}`;
      })
      .join("");
    parts.push(`\\trowd\\trgaph0\\trleft0${pad}${headDefs}`);
    parts.push(
      model.columnLabels
        .map(
          (label) =>
            `\\pard\\intbl\\fs${FS_COL_HEAD}\\cf${colors.index(MUTED)}\\b ${rtfEscape(label.toUpperCase())}\\b0\\cf${colors.index(INK)}\\cell`,
        )
        .join("") + "\\row",
    );

    for (const row of scene.rows) {
      const defs = row.cells
        .map((cell, i) => {
          const right = model.showColumnRule && i === 0 && colCount > 1;
          const bottom = cell.merge === "restart" && (cell.ruleBelow || row.ruleBelow);
          const merge =
            cell.merge === "restart" && cell.span > 1
              ? "\\clvmgf"
              : cell.merge === "continue"
                ? "\\clvmrg"
                : "";
          return `${cellBorders({ bottom, right, colors })}${merge}\\clvertalt\\cellx${xs[i]}`;
        })
        .join("");
      parts.push(`\\trowd\\trgaph0\\trleft0${pad}${defs}`);
      parts.push(row.cells.map((cell) => `${cellContent(cell, colors)}\\cell`).join("") + "\\row");
    }
    parts.push("\\pard\\sa480\\par");
  }
  return parts.join("");
}

function preamble(colors: RtfColors): string {
  return [
    "{\\rtf1\\ansi\\deff0\\uc1",
    `{\\fonttbl{\\f0\\fswiss ${FONT_SANS};}{\\f1\\fswiss ${FONT_SANS_FALLBACK};}}`,
    colors.table(),
    `\\paperw${PAGE_W_TWIP}\\paperh${PAGE_H_TWIP}` +
      `\\margl${MARGIN_LEFT_TWIP}\\margr${MARGIN_RIGHT_TWIP}` +
      `\\margt${MARGIN_TOP_TWIP}\\margb${MARGIN_BOTTOM_TWIP}` +
      `\\headery${HEADER_TWIP}\\footery${FOOTER_TWIP}`,
    `\\f0\\fs${FS_BODY}\\cf${colors.index(INK)}`,
  ].join("");
}

/**
 * Two-column manuscript RTF matching Forbered udskrift / PDF page setup.
 */
export function exportEpisodeToRtf(
  doc: ScriptDocument,
  episodeId?: Id | null,
  options: ManuscriptExportOptions = {},
): string {
  const model = buildManuscriptExport(doc, episodeId, options);
  const colors = new RtfColors();
  colors.index(INK);
  colors.index(MUTED);
  colors.index(ACCENT);
  colors.index(BORDER);
  colors.index(RULE);
  if (!model) {
    return `${preamble(colors)} }`;
  }
  for (const scene of model.scenes) colors.index(scene.headerBg);
  return (
    preamble(colors) +
    rtfHeaderFooter(model, colors) +
    scriptTableRtf(model, colors) +
    "}"
  );
}

/**
 * Audio column as flowing text (talent sides), with the same page chrome as print.
 */
export function exportEpisodeAudioToRtf(
  doc: ScriptDocument,
  episodeId?: Id | null,
  options: ManuscriptExportOptions = {},
): string {
  const model = buildManuscriptExport(doc, episodeId, options);
  const colors = new RtfColors();
  colors.index(INK);
  colors.index(MUTED);
  colors.index(ACCENT);
  colors.index(BORDER);
  colors.index(RULE);
  if (!model) return `${preamble(colors)} }`;
  for (const scene of model.scenes) colors.index(scene.headerBg);

  const parts: string[] = [preamble(colors), rtfHeaderFooter(model, colors)];
  for (const scene of model.scenes) {
    parts.push(sceneHeadRtf(scene, colors));
    parts.push("\\pard\\par");
    let wrote = false;
    for (const row of scene.rows) {
      const audio = model.audioOnly ? row.cells[0] : row.cells[row.cells.length - 1];
      if (!audio || audio.merge === "continue") continue;
      const empty =
        !audio.characterName &&
        (!audio.blocks.length ||
          audio.blocks.every((b) => b.runs.every((r) => !r.text.trim())));
      if (empty) continue;
      if (wrote) parts.push("\\pard\\par");
      if (audio.characterName) {
        parts.push(
          `\\pard\\sl${LINE_240}\\slmult1\\fs${FS_CHARACTER}\\b ${rtfEscape(audio.characterName)}\\b0\\par`,
        );
      }
      parts.push(
        blocksToRtf(audio.blocks, colors, {
          intbl: false,
          color: audio.color,
        }),
      );
      wrote = true;
    }
    parts.push("\\pard\\sa480\\par");
  }
  parts.push("}");
  return parts.join("");
}

export function suggestedRtfFilename(
  doc: ScriptDocument,
  episodeId?: Id | null,
  variant: "full" | "audio" = "full",
): string {
  return suggestedExportFilename(doc, episodeId, variant, "rtf");
}

/** Kept for callers that imported the old helper. */
export { suggestedExportFilename };
